import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PendingDecision } from '@/components/editorial/PendingDecision';
import { resolveMsg, type TKey } from '@/i18n/msg';
import { BROWSER_ZONE_VALUE, TIMEZONE_GROUPS, getUtcOffset } from './timezone-zones';

/**
 * The Timezone control, shared by the parent Settings page (Account tab) and
 * staff Settings (#2741), so the two cannot drift.
 *
 * `value` is the stored setting: an IANA zone, or '' for "never chose one",
 * which means staff times follow the browser's zone. The first item says so
 * in words and shows the zone the browser is in right now.
 */
export function TimezoneField({
  value,
  onChange,
  variant = 'family',
}: {
  value: string;
  onChange: (zone: string) => void;
  /** Which helper line to show: staff read times in this zone; families use it to schedule. */
  variant?: 'family' | 'staff';
}) {
  const { t } = useTranslation(['settings']);
  const label = (key: TKey) => resolveMsg(t, { key });
  const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const browserOffset = browserZone ? getUtcOffset(browserZone) : '';

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5">
        <Label htmlFor="timezone">{t('account.timezone.label')}</Label>
        {/* #2421: the open list's edge gap is a default every Select shares. */}
        <PendingDecision id="select-list-edge-gap" side="right" />
      </div>
      <Select
        value={value || BROWSER_ZONE_VALUE}
        onValueChange={(next) => onChange(next === BROWSER_ZONE_VALUE ? '' : next)}
      >
        <SelectTrigger id="timezone">
          <SelectValue placeholder={t('account.timezone.placeholder')} />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectItem value={BROWSER_ZONE_VALUE}>
            {t('account.timezone.browser')}{' '}
            {browserZone && (
              <span className="text-stevens-ink-3">
                ({browserZone}{browserOffset ? `, ${browserOffset}` : ''})
              </span>
            )}
          </SelectItem>
          <SelectSeparator />
          {TIMEZONE_GROUPS.map((group) => (
            <SelectGroup key={group.labelKey}>
              <SelectLabel>{label(group.labelKey)}</SelectLabel>
              {group.zones.map((zone) => (
                <SelectItem key={zone.value} value={zone.value}>
                  {label(zone.labelKey)}{' '}
                  <span className="text-stevens-ink-3">
                    ({getUtcOffset(zone.value)})
                  </span>
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
      <p className="text-caption text-stevens-ink-3">
        {variant === 'staff' ? t('account.timezone.helperStaff') : t('account.timezone.helper')}
      </p>
    </div>
  );
}
